import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Plus, Info } from 'lucide-react';
import { useAddPly, useUpdatePly } from '@/hooks/usePlybooks';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import type { PlybookPly, PlyFormData, MaterialType, PlybookZone } from '@/types/plybook';
import {
  FIBER_ANGLES,
  formatFiberAngle,
  PLY_COLORS,
  MATERIAL_TYPE_LABELS,
} from '@/types/plybook';

interface PlyEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plybookId: string;
  ply?: PlybookPly | null;
  nextPlyNumber?: number;
  onSuccess?: () => void;
}

export function PlyEditor({
  open,
  onOpenChange,
  plybookId,
  ply,
  nextPlyNumber = 1,
  onSuccess,
}: PlyEditorProps) {
  const isEditing = !!ply;
  const addMutation = useAddPly();
  const updateMutation = useUpdatePly();

  const [form, setForm] = useState<PlyFormData>({
    ply_number: ply?.ply_number || nextPlyNumber,
    material_id: ply?.material_id || '',
    material_name: ply?.material_name || '',
    material_type: ply?.material_type || 'carbon_fiber',
    fiber_angle: ply?.fiber_angle || 0,
    orientation: ply?.orientation || 'warp',
    width_mm: ply?.width_mm || undefined,
    length_mm: ply?.length_mm || undefined,
    area_m2: ply?.area_m2 || undefined,
    weight_grams: ply?.weight_grams || undefined,
    zone: ply?.zone || '',
    is_full_coverage: ply?.is_full_coverage ?? true,
    special_instructions: ply?.special_instructions || '',
    color: ply?.color || PLY_COLORS.carbon_fiber,
  });

  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [showAddZone, setShowAddZone] = useState(false);
  const [newZoneName, setNewZoneName] = useState('');
  const [newZoneColor, setNewZoneColor] = useState('#3b82f6');

  // Load materials from inventory
  const { data: materials } = useQuery({
    queryKey: ['materials-for-ply'],
    queryFn: async () => {
      const { data, error } = await api
        .from('material_inventory')
        .select('id, name, category')
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data;
    },
  });

  // Load zones assigned to this specific plybook
  const { data: plybookZones, isLoading: zonesLoading } = useQuery({
    queryKey: ['plybook-zone-positions', plybookId],
    queryFn: async () => {
      const { data, error } = await api
        .from('plybook_zone_positions')
        .select('id, zone_id, zone:plybook_zones(id, name, color, sort_order)')
        .eq('plybook_id', plybookId)
        .order('created_at');
      if (error) throw error;
      return data;
    },
    enabled: !!plybookId,
  });

  // Default zone when plybook has no zones assigned
  const DEFAULT_ZONE: PlybookZone = {
    id: 'default-zona-0',
    name: 'Zona 0',
    color: '#6b7280',
    sort_order: 0,
    is_default: true,
    is_active: true,
  };

  // Transform to PlybookZone[] format, fallback to default if empty
  const zones: PlybookZone[] = plybookZones?.length
    ? plybookZones.map(pz => ({
        id: pz.zone?.id || pz.zone_id,
        name: pz.zone?.name || 'Sin nombre',
        color: pz.zone?.color || '#6b7280',
        sort_order: pz.zone?.sort_order || 0,
        is_default: false,
        is_active: true,
      }))
    : [DEFAULT_ZONE];

  // Add new zone mutation - creates zone and assigns to this plybook
  const addZoneMutation = useMutation({
    mutationFn: async ({ name, color }: { name: string; color: string }) => {
      const maxOrder = zones?.length ? Math.max(...zones.map(z => z.sort_order)) : 0;
      // 1. Create the zone in plybook_zones
      const { data: newZone, error: zoneError } = await api
        .from('plybook_zones')
        .insert([{ name, color, sort_order: maxOrder + 1 }])
        .select()
        .single();
      if (zoneError) throw zoneError;

      // 2. Assign it to this plybook in plybook_zone_positions
      const { error: posError } = await api
        .from('plybook_zone_positions')
        .insert([{
          plybook_id: plybookId,
          zone_id: newZone.id,
          position_x: 50, // Centro por defecto
          position_y: 50,
        }]);
      if (posError) throw posError;

      return newZone as PlybookZone;
    },
    onSuccess: (newZone) => {
      queryClient.invalidateQueries({ queryKey: ['plybook-zone-positions', plybookId] });
      setForm(prev => ({ ...prev, zone: newZone.name }));
      setShowAddZone(false);
      setNewZoneName('');
      setNewZoneColor('#3b82f6');
      toast({ title: 'Zona creada', description: `Se agregó "${newZone.name}" al plybook` });
    },
    onError: (error: any) => {
      toast({
        title: 'Error al crear zona',
        description: error.message || 'No se pudo guardar la zona',
        variant: 'destructive',
      });
    },
  });

  // Get default zone on mount
  const defaultZone = zones?.find(z => z.is_default)?.name || zones?.[0]?.name || '';

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setForm({
        ply_number: ply?.ply_number || nextPlyNumber,
        material_id: ply?.material_id || '',
        material_name: ply?.material_name || '',
        material_type: ply?.material_type || 'carbon_fiber',
        fiber_angle: ply?.fiber_angle || 0,
        orientation: ply?.orientation || 'warp',
        width_mm: ply?.width_mm || undefined,
        length_mm: ply?.length_mm || undefined,
        area_m2: ply?.area_m2 || undefined,
        weight_grams: ply?.weight_grams || undefined,
        zone: ply?.zone || defaultZone || '',
        is_full_coverage: ply?.is_full_coverage ?? true,
        special_instructions: ply?.special_instructions || '',
        color: ply?.color || PLY_COLORS.carbon_fiber,
      });
      setShowAddZone(false);
    }
  }, [open, ply, nextPlyNumber, defaultZone]);

  // Update color when material type changes
  const handleMaterialTypeChange = (type: MaterialType) => {
    setForm((prev) => ({
      ...prev,
      material_type: type,
      color: PLY_COLORS[type],
    }));
  };

  // Calculate area when dimensions change
  const handleDimensionChange = (field: 'width_mm' | 'length_mm', value: number | undefined) => {
    setForm((prev) => {
      const newForm = { ...prev, [field]: value };
      if (newForm.width_mm && newForm.length_mm) {
        newForm.area_m2 = (newForm.width_mm * newForm.length_mm) / 1000000;
      }
      return newForm;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (isEditing && ply) {
        await updateMutation.mutateAsync({
          id: ply.id,
          plybookId,
          data: form,
        });
      } else {
        await addMutation.mutateAsync({
          plybookId,
          data: form,
        });
      }
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      // Error handled by mutation
    }
  };

  const isLoading = addMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? `Editar Capa #${ply.ply_number}` : `Nueva Capa #${nextPlyNumber}`}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Material Selection */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Material del Inventario</Label>
              <Select
                value={form.material_id || 'none'}
                onValueChange={(v) => {
                  const mat = materials?.find((m) => m.id === v);
                  setForm((prev) => ({
                    ...prev,
                    material_id: v === 'none' ? '' : v,
                    material_name: mat?.name || prev.material_name,
                  }));
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Manual</SelectItem>
                  {materials?.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Nombre del Material</Label>
              <Input
                value={form.material_name}
                onChange={(e) => setForm((prev) => ({ ...prev, material_name: e.target.value }))}
                placeholder="Ej: Carbono 3K"
              />
            </div>
          </div>

          {/* Material Type & Fiber Angle */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Tipo de Material</Label>
              <Select
                value={form.material_type}
                onValueChange={(v) => handleMaterialTypeChange(v as MaterialType)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(MATERIAL_TYPE_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded"
                          style={{ backgroundColor: PLY_COLORS[value as MaterialType] }}
                        />
                        {label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Ángulo de Fibra (°)</Label>
              <Select
                value={String(form.fiber_angle)}
                onValueChange={(v) => setForm((prev) => ({ ...prev, fiber_angle: Number(v) }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIBER_ANGLES.map((angle) => (
                    <SelectItem key={angle} value={String(angle)}>
                      {formatFiberAngle(angle)}
                    </SelectItem>
                  ))}
                  <SelectItem value="custom">Personalizado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Zone & Coverage */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Zona del Molde</Label>
              {form.is_full_coverage ? (
                <div className="p-2.5 rounded-md bg-blue-50 border border-blue-200 text-sm text-blue-700 dark:bg-blue-950 dark:border-blue-800 dark:text-blue-300">
                  <Info className="w-4 h-4 inline mr-1.5" />
                  Es la suma de todas las Zonas
                </div>
              ) : !showAddZone ? (
                <Select
                  value={form.zone || defaultZone}
                  onValueChange={(v) => {
                    if (v === '__add_new__') {
                      setShowAddZone(true);
                    } else {
                      const zone = zones?.find(z => z.name === v);
                      setForm((prev) => ({ ...prev, zone: v, color: zone?.color || prev.color }));
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar zona..." />
                  </SelectTrigger>
                  <SelectContent>
                    {zones?.map((zone) => (
                      <SelectItem key={zone.id} value={zone.name}>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: zone.color }}
                          />
                          {zone.name}
                        </div>
                      </SelectItem>
                    ))}
                    <SelectItem value="__add_new__">
                      <div className="flex items-center gap-2 text-primary">
                        <Plus className="w-3 h-3" />
                        Agregar zona...
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Input
                      value={newZoneName}
                      onChange={(e) => setNewZoneName(e.target.value)}
                      placeholder="Nombre de la zona"
                      className="flex-1"
                    />
                    <input
                      type="color"
                      value={newZoneColor}
                      onChange={(e) => setNewZoneColor(e.target.value)}
                      className="w-10 h-9 rounded border cursor-pointer"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setShowAddZone(false)}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => {
                        const nameExists = zones?.some(z => z.name.toLowerCase() === newZoneName.trim().toLowerCase());
                        if (nameExists) {
                          toast({
                            title: 'Nombre duplicado',
                            description: `Ya existe una zona llamada "${newZoneName.trim()}"`,
                            variant: 'destructive',
                          });
                          return;
                        }
                        addZoneMutation.mutate({ name: newZoneName, color: newZoneColor });
                      }}
                      disabled={!newZoneName.trim() || addZoneMutation.isPending}
                    >
                      {addZoneMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Guardar'}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between pt-6">
              <Label>Cobertura Total</Label>
              <Switch
                checked={form.is_full_coverage}
                onCheckedChange={(v) => {
                  setForm((prev) => ({ ...prev, is_full_coverage: v, zone: v ? '' : prev.zone }));
                  if (v) {
                    toast({ title: 'Cobertura Total', description: 'Es la suma de todas las Zonas' });
                  }
                }}
              />
            </div>
          </div>

          {/* Dimensions */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label>Ancho (mm)</Label>
              <Input
                type="number"
                value={form.width_mm || ''}
                onChange={(e) =>
                  handleDimensionChange('width_mm', e.target.value ? Number(e.target.value) : undefined)
                }
                placeholder="0"
                step="0.1"
              />
            </div>

            <div>
              <Label>Largo (mm)</Label>
              <Input
                type="number"
                value={form.length_mm || ''}
                onChange={(e) =>
                  handleDimensionChange('length_mm', e.target.value ? Number(e.target.value) : undefined)
                }
                placeholder="0"
                step="0.1"
              />
            </div>

            <div>
              <Label>Área (m²)</Label>
              <Input
                type="number"
                value={form.area_m2 || ''}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, area_m2: e.target.value ? Number(e.target.value) : undefined }))
                }
                placeholder="0.0000"
                step="0.0001"
              />
            </div>
          </div>

          {/* Weight */}
          <div>
            <Label>Peso (gramos)</Label>
            <Input
              type="number"
              value={form.weight_grams || ''}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  weight_grams: e.target.value ? Number(e.target.value) : undefined,
                }))
              }
              placeholder="0"
              step="0.1"
            />
          </div>

          {/* Special Instructions */}
          <div>
            <Label>Instrucciones Especiales</Label>
            <Textarea
              value={form.special_instructions}
              onChange={(e) => setForm((prev) => ({ ...prev, special_instructions: e.target.value }))}
              placeholder="Ej: Aplicar resina adicional en bordes..."
              rows={2}
            />
          </div>

          {/* Color Preview */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
            <div
              className="w-12 h-12 rounded-lg shadow-sm"
              style={{ backgroundColor: form.color }}
            />
            <div>
              <p className="text-sm font-medium">Vista previa del color</p>
              <p className="text-xs text-muted-foreground">
                Este color se usará en la visualización del stack
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isEditing ? 'Guardar' : 'Agregar Capa'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
